import type React from 'react'
import { useEffect, useState } from 'react'

import { Alert, Button, Typography } from 'components'
import { ROUTES } from 'constants/urls'
import { AuthLayout } from 'layouts/AuthLayout'
import ReactCodeInput from 'react-code-input'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { sendCodeRequest, validateCodeRequest } from 'services/forgetPassword'

import type { CodeResponseStorage } from './components/types/responses'
import * as S from './styles'

const { forgetPassword } = ROUTES

const ValidateCode: React.FC = () => {
  const [code, setCode] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [showInvalidCodeError, setShowInvalidCodeError] = useState<boolean>(false)
  const [resendCode, setResendCode] = useState<boolean>(false)
  const isButtonDisabled = code === '' || code.length < 6

  const email = localStorage.getItem('resetPassword')
  const emailFromStorage: CodeResponseStorage = email !== null && JSON.parse(email)

  const navigate = useNavigate()

  useEffect(() => {
    if (email === null) {
      navigate(forgetPassword.base)
    }
  }, [])

  const handleInputChange = (value: string) => {
    setShowInvalidCodeError(false)
    setCode(value)
  }

  const handleResendCode = async () => {
    await sendCodeRequest({
      ds_emai: emailFromStorage.email,
    })
    toast.success('Código enviado com sucesso!')
  }

  const handleValidateCode = async (event: React.FormEvent) => {
    event.preventDefault()
    setLoading(true)
    try {
      const response = await validateCodeRequest({
        ds_cod_senh: code,
        id_usua: emailFromStorage.id_usua,
      })

      const { return_code } = response

      if (return_code === 6010) {
        setShowInvalidCodeError(true)
        return
      }
      if (return_code === 6011) {
        toast.error('Código expirado. Por favor, solicite um código novamente.')
        setResendCode(true)
        return
      }

      localStorage.setItem(
        'resetPassword',
        JSON.stringify({
          ...emailFromStorage,
          code: code,
          return_chav: 'Sucesso',
        }),
      )
      navigate(forgetPassword.getLink('resetPassword'))
    } catch (error) {
      toast.error('Erro no código de validação')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout>
      <form autoComplete="true" onSubmit={handleValidateCode}>
        <S.FieldsWrapper>
          <Typography className="forgetTitle" design="hav-h5-text">
            Verificação
          </Typography>

          <Typography className="forgetDescription">
            Insira o código que foi enviado para e-mail:
            <br />
            <strong>{emailFromStorage.email.toLowerCase()}</strong>
            <br />
            nos campos abaixo.
          </Typography>
          {showInvalidCodeError && (
            <S.ErrorWrapper>
              <Alert
                fullWidth
                kind="error"
                message="Verifique o código digitado e tente novamente."
                title="Código incorreto."
              />
            </S.ErrorWrapper>
          )}
          <ReactCodeInput
            fields={6}
            forceUppercase
            inputMode="latin"
            inputStyle={{
              border: '1px solid #BCBCBC',
              borderRadius: '5px',
              height: '40px',
              marginRight: '10px',
              textAlign: 'center',
              width: '40px',
            }}
            inputStyleInvalid={{
              border: '1px solid #FF0000',
              borderRadius: '5px',
              height: '40px',
              margin: '2.5px',
              textAlign: 'center',
              width: '40px',
            }}
            isValid={!showInvalidCodeError}
            name="code"
            onChange={handleInputChange}
            placeholder="-"
            type="text"
          />
        </S.FieldsWrapper>
        <S.ButtonWrapper>
          <Button
            content="Confirmar"
            disabled={isButtonDisabled}
            fullWidth
            loading={loading}
            type="submit"
          />
        </S.ButtonWrapper>
        {resendCode && <S.ResendCode onClick={handleResendCode}>Reenviar código</S.ResendCode>}
      </form>
    </AuthLayout>
  )
}

export default ValidateCode
